import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
    AUTO_CLOSE_ERROR_MS,
    AUTO_CLOSE_INFO_MS,
    AUTO_CLOSE_SUCCESS_MS,
} from 'src/constants';

import {Message} from '@pb/kit-component-message';

import {MessageContext} from './message-context';


type Props = {
    children: React.ReactNode,
};

export const parseErrorMessages = (errorMessage: string) => {
    const messages = errorMessage.split('\n');
    if (messages.length > 1) {
        return messages.map((e, idx) => (
            <div key={idx}>{e}</div>
        ));
    }

    return errorMessage;
};

type TimerRef = React.RefObject<ReturnType<typeof setTimeout> | null> & {
    current: ReturnType<typeof setTimeout> | null,
};

export const MessageContextProvider = ({children}: Props) => {
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    const successTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null) as TimerRef;
    const errorTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null) as TimerRef;
    const infoTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null) as TimerRef;

    const clearTimer = useCallback((ref: TimerRef) => {
        if (ref.current) {
            clearTimeout(ref.current);
            ref.current = null;
        }
    }, []);

    const scheduleAutoClose = useCallback((
        message: string,
        close: () => void,
        ref: TimerRef,
        delayMs: number,
    ) => {
        clearTimer(ref);
        if (!message) {
            return;
        }
        ref.current = setTimeout(() => {
            close();
            ref.current = null;
        }, delayMs);
    }, [clearTimer]);

    const onCloseSuccessMessage = useCallback(() => {
        setSuccessMessage('');
        clearTimer(successTimeoutIdRef);
    }, [clearTimer]);

    const onCloseErrorMessage = useCallback(() => {
        setErrorMessage('');
        clearTimer(errorTimeoutIdRef);
    }, [clearTimer]);

    const onCloseInfoMessage = useCallback(() => {
        setInfoMessage('');
        clearTimer(infoTimeoutIdRef);
    }, [clearTimer]);

    useEffect(() => {
        scheduleAutoClose(successMessage, onCloseSuccessMessage, successTimeoutIdRef, AUTO_CLOSE_SUCCESS_MS);
    }, [successMessage, onCloseSuccessMessage, scheduleAutoClose]);

    useEffect(() => {
        scheduleAutoClose(errorMessage, onCloseErrorMessage, errorTimeoutIdRef, AUTO_CLOSE_ERROR_MS);
    }, [errorMessage, onCloseErrorMessage, scheduleAutoClose]);

    useEffect(() => {
        scheduleAutoClose(infoMessage, onCloseInfoMessage, infoTimeoutIdRef, AUTO_CLOSE_INFO_MS);
    }, [infoMessage, onCloseInfoMessage, scheduleAutoClose]);

    useEffect(() => () => {
        clearTimer(successTimeoutIdRef);
        clearTimer(errorTimeoutIdRef);
        clearTimer(infoTimeoutIdRef);
    }, [clearTimer]);

    const value = useMemo(() => ({
        setErrorMessage,
        setSuccessMessage,
        setInfoMessage,
    }), [setErrorMessage, setSuccessMessage, setInfoMessage]);

    const hasAnyMessage = Boolean(successMessage || errorMessage || infoMessage);

    return (
        <>
            <MessageContext.Provider value={value}>
                {children}
            </MessageContext.Provider>

            {hasAnyMessage ? (
                <div className="message-overlay" role="status" aria-live="polite">
                    <div className="message-overlay__content">
                        {successMessage ? (
                            <Message color="success" onClose={onCloseSuccessMessage}>
                                {successMessage}
                            </Message>
                        ) : null}
                        {errorMessage ? (
                            <Message color="error" onClose={onCloseErrorMessage}>
                                {parseErrorMessages(errorMessage)}
                            </Message>
                        ) : null}
                        {infoMessage ? (
                            <Message color="info" onClose={onCloseInfoMessage}>
                                {infoMessage}
                            </Message>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </>
    );
};
